/**
 * PD-16475 — notifications for storefront (portal) users.
 *
 * Portal users (`public_user`) get their own notification tables
 * (`public_user_notification*`) rather than sharing the CRM user's
 * `user_notification*` tables: the two user tables share one integer id space,
 * so a single `fk_user__id` column could not tell a customer from an employee.
 * The catalog, channels and default rules for the portal side live here, next
 * to (but separate from) the CRM catalog in notification.constants.ts.
 */

/** The events a portal user can be notified about. */
export const PORTAL_NOTIFICATION_TYPE = {
	/** Staff posted a customer-visible reply on a support request the user submitted. */
	ISSUE_REPLY: 'portal_issue_reply',
	/** A support request the user submitted was resolved, reopened or started. */
	ISSUE_STATUS_CHANGED: 'portal_issue_status_changed',
	/**
	 * PD-16845 — another buyer on one of the user's accounts asked them to
	 * approve a cart that is over the asker's approval limit. Bell and push
	 * only: the branded "Order Approval Requested" email is sent separately
	 * (the legacy shop always sent it), so it is not one of the events the
	 * user controls in their notification settings.
	 */
	ORDER_APPROVAL_REQUESTED: 'portal_order_approval_requested',
	/**
	 * PD-16863 — a statement run or cycle billing generated a statement for an
	 * account the user can open: a bell row and a text.
	 *
	 * PD-16902 — and Email, which is the customer's own statement email (PDF
	 * attached), sent to each storefront user when the customer has
	 * "Storefront users" ticked. The statement run sends it, never the
	 * notification dispatcher, so the user's Email box decides whether they
	 * are one of its recipients rather than sending a second, shorter email.
	 */
	STATEMENT_AVAILABLE: 'portal_statement_available',
} as const;

export type PortalNotificationType =
	(typeof PORTAL_NOTIFICATION_TYPE)[keyof typeof PORTAL_NOTIFICATION_TYPE];

/** Broad bucket stored on the bell row (drives the tray icon). */
export const PORTAL_NOTIFICATION_CATEGORY = {
	SUPPORT: 'support',
	/** PD-16845 — ordering events, such as an approval request. */
	ORDERS: 'orders',
	/** PD-16863 — account billing events, such as a new statement. */
	BILLING: 'billing',
} as const;

export type PortalNotificationCategory =
	(typeof PORTAL_NOTIFICATION_CATEGORY)[keyof typeof PORTAL_NOTIFICATION_CATEGORY];

/**
 * PD-16863 — the heading each category's events sit under on the storefront's
 * notification settings page.
 */
export const PORTAL_NOTIFICATION_CATEGORY_LABELS: Readonly<
	Record<PortalNotificationCategory, string>
> = {
	support: 'Support requests',
	orders: 'Orders',
	billing: 'Statements',
};

/**
 * Delivery channels for a portal user. Native app push is deliberately absent
 * (deferred to PD-14302); adding it later means a new value here and in
 * `ALL_PORTAL_NOTIFICATION_CHANNELS`, a new column on
 * `public_user_notification_preference`, and a branch in
 * `resolvePortalNotificationChannels` — nothing else keys off the full list.
 */
export const PORTAL_NOTIFICATION_CHANNEL = {
	IN_APP: 'in_app',
	EMAIL: 'email',
	SMS: 'sms',
	PUSH: 'push',
} as const;

export type PortalNotificationChannel =
	(typeof PORTAL_NOTIFICATION_CHANNEL)[keyof typeof PORTAL_NOTIFICATION_CHANNEL];

const ALL_PORTAL_NOTIFICATION_CHANNELS: ReadonlyArray<PortalNotificationChannel> =
	[
		PORTAL_NOTIFICATION_CHANNEL.IN_APP,
		PORTAL_NOTIFICATION_CHANNEL.EMAIL,
		PORTAL_NOTIFICATION_CHANNEL.SMS,
		PORTAL_NOTIFICATION_CHANNEL.PUSH,
	];

/**
 * PD-16863 — what a text for an event needs besides a phone and no STOP.
 *
 * - `explicit`: recorded consent for the account's current phone, given by
 *   ticking a Text box next to the disclosure (PD-16475). Every event that
 *   predates statements works this way.
 * - `implied`: none. Owner decision (PD-16863): statement texts go to every
 *   storefront user with a phone number unless they untick Text for the
 *   event or reply STOP. Every text still ends with the STOP line.
 */
export const PORTAL_SMS_CONSENT_POLICY = {
	EXPLICIT: 'explicit',
	IMPLIED: 'implied',
} as const;

export type PortalSmsConsentPolicy =
	(typeof PORTAL_SMS_CONSENT_POLICY)[keyof typeof PORTAL_SMS_CONSENT_POLICY];

export interface PortalNotificationEventModel {
	type: PortalNotificationType;
	category: PortalNotificationCategory;
	/** Row label in the storefront's notification settings. */
	label: string;
	description: string;
	/**
	 * PD-16863 — the channels this event is ever delivered on, which are also
	 * the only boxes the settings page offers for it. A channel left out never
	 * sends, whatever the user's saved preference row says.
	 */
	channels: ReadonlyArray<PortalNotificationChannel>;
	/** PD-16863 — see {@link PORTAL_SMS_CONSENT_POLICY}. */
	smsConsent: PortalSmsConsentPolicy;
}

/** Every event a portal user can control, in display order. */
export const PORTAL_NOTIFICATION_EVENTS: ReadonlyArray<PortalNotificationEventModel> =
	[
		{
			type: PORTAL_NOTIFICATION_TYPE.ISSUE_REPLY,
			category: PORTAL_NOTIFICATION_CATEGORY.SUPPORT,
			label: 'Replies to my support requests',
			description: 'When our team replies to a support request you submitted.',
			channels: ALL_PORTAL_NOTIFICATION_CHANNELS,
			smsConsent: PORTAL_SMS_CONSENT_POLICY.EXPLICIT,
		},
		{
			type: PORTAL_NOTIFICATION_TYPE.ISSUE_STATUS_CHANGED,
			category: PORTAL_NOTIFICATION_CATEGORY.SUPPORT,
			label: 'Status changes on my support requests',
			description:
				'When a support request you submitted is resolved, reopened or started.',
			channels: ALL_PORTAL_NOTIFICATION_CHANNELS,
			smsConsent: PORTAL_SMS_CONSENT_POLICY.EXPLICIT,
		},
		{
			type: PORTAL_NOTIFICATION_TYPE.STATEMENT_AVAILABLE,
			category: PORTAL_NOTIFICATION_CATEGORY.BILLING,
			label: 'New statements',
			description: 'When a new statement is ready for one of your accounts.',
			channels: [
				PORTAL_NOTIFICATION_CHANNEL.IN_APP,
				PORTAL_NOTIFICATION_CHANNEL.EMAIL,
				PORTAL_NOTIFICATION_CHANNEL.SMS,
			],
			smsConsent: PORTAL_SMS_CONSENT_POLICY.IMPLIED,
		},
	];

/** The catalog entry for `type`, or null for an unlisted (or unknown) type. */
export function portalNotificationEventFor(
	type: string,
): PortalNotificationEventModel | null {
	return PORTAL_NOTIFICATION_EVENTS.find((event) => event.type === type) ?? null;
}

/**
 * The delivery rules `resolvePortalNotificationChannels` applies for `type`.
 * An unlisted type (such as an approval request, which the user does not
 * control) keeps every channel and the explicit text consent rule, which is
 * how every event was delivered before events carried their own channels.
 */
export function portalNotificationDeliveryRulesFor(type: string): {
	channels: ReadonlyArray<PortalNotificationChannel>;
	smsConsent: PortalSmsConsentPolicy;
} {
	const event = portalNotificationEventFor(type);
	return {
		channels: event?.channels ?? ALL_PORTAL_NOTIFICATION_CHANNELS,
		smsConsent: event?.smsConsent ?? PORTAL_SMS_CONSENT_POLICY.EXPLICIT,
	};
}

/** The support events the inline "Notify me about replies" control sets together. */
export const PORTAL_SUPPORT_NOTIFICATION_TYPES: ReadonlyArray<PortalNotificationType> =
	[
		PORTAL_NOTIFICATION_TYPE.ISSUE_REPLY,
		PORTAL_NOTIFICATION_TYPE.ISSUE_STATUS_CHANGED,
	];

export function isPortalNotificationType(
	value: unknown,
): value is PortalNotificationType {
	return (
		typeof value === 'string' &&
		PORTAL_NOTIFICATION_EVENTS.some((event) => event.type === value)
	);
}

/**
 * Categories of the events that are not in the settings catalog above, so
 * their bell rows still get the right tray icon.
 */
const PORTAL_UNLISTED_NOTIFICATION_CATEGORIES: Readonly<
	Partial<Record<string, PortalNotificationCategory>>
> = {
	[PORTAL_NOTIFICATION_TYPE.ORDER_APPROVAL_REQUESTED]:
		PORTAL_NOTIFICATION_CATEGORY.ORDERS,
};

export function portalNotificationCategoryFor(
	type: string,
): PortalNotificationCategory {
	return (
		PORTAL_NOTIFICATION_EVENTS.find((event) => event.type === type)?.category ??
		(Object.prototype.hasOwnProperty.call(
			PORTAL_UNLISTED_NOTIFICATION_CATEGORIES,
			type,
		)
			? PORTAL_UNLISTED_NOTIFICATION_CATEGORIES[type]
			: undefined) ??
		PORTAL_NOTIFICATION_CATEGORY.SUPPORT
	);
}

/**
 * Appended to every notification text. Carriers require an opt-out
 * instruction on automated texts, and a STOP reply is honored both by the
 * carrier and by our own stored opt-out.
 */
export const PORTAL_SMS_OPT_OUT_SUFFIX = 'Reply STOP to opt out.';

/** Shown under every "Text" checkbox a portal user can tick (the consent disclosure). */
export const PORTAL_SMS_DISCLOSURE =
	'Msg & data rates may apply. Reply STOP to opt out.';

/** One explicit preference row's channel flags. */
export interface PortalNotificationChannelFlagsModel {
	inAppEnabled: boolean;
	emailEnabled: boolean;
	smsEnabled: boolean;
	pushEnabled: boolean;
}

/** The per-user master controls and SMS consent state that gate delivery. */
export interface PortalNotificationSettingStateModel {
	notificationsPaused: boolean;
	/** E.164 phone the user agreed to receive texts on; null = no consent recorded. */
	smsConsentPhone: string | null;
	smsConsentDate: Date | string | null;
	/** Set when the user replied STOP (or a send was refused as opted out). */
	smsOptedOutDate: Date | string | null;
	/** E.164 phone the STOP came from; null = applies to any phone. */
	smsOptedOutPhone: string | null;
}

export interface ResolvePortalNotificationChannelsInputModel {
	/** The user's saved row for this event, or null to use the defaults. */
	explicitPreference: PortalNotificationChannelFlagsModel | null;
	setting: PortalNotificationSettingStateModel | null;
	hasEmail: boolean;
	/** The account's phone in E.164, or null when it has none (or it cannot be read). */
	phoneE164: string | null;
	/** Whether the user has subscribed at least one browser for push. */
	hasPushDevice: boolean;
	/**
	 * PD-16863 — the channels the event is delivered on at all; omitted =
	 * every channel. Use `portalNotificationDeliveryRulesFor`.
	 */
	supportedChannels?: ReadonlyArray<PortalNotificationChannel>;
	/** PD-16863 — omitted = `explicit`, the rule every event had before. */
	smsConsent?: PortalSmsConsentPolicy;
}

export interface PortalNotificationChannelsModel {
	inApp: boolean;
	email: boolean;
	sms: boolean;
	push: boolean;
}

/**
 * The channel values shown to a user who has never saved a preference for an
 * event (owner decision, PD-16475): the bell always; email when the account
 * has an email address; texts when it has a phone number (the Text box is
 * pre-checked, and consent is recorded when the user saves or submits with it
 * ticked); browser push once they have subscribed a browser, because
 * subscribing is itself the opt-in.
 */
export function defaultPortalNotificationChannels(args: {
	hasEmail: boolean;
	hasPhone: boolean;
	hasPushDevice: boolean;
}): PortalNotificationChannelsModel {
	return {
		inApp: true,
		email: args.hasEmail,
		sms: args.hasPhone,
		push: args.hasPushDevice,
	};
}

/** Whether texting `phoneE164` was agreed to (consent is per phone number). */
export function hasPortalSmsConsent(
	setting: PortalNotificationSettingStateModel | null,
	phoneE164: string | null,
): boolean {
	return (
		setting != null &&
		phoneE164 != null &&
		setting.smsConsentDate != null &&
		setting.smsConsentPhone === phoneE164
	);
}

/**
 * Whether the user has opted out of texts on `phoneE164`. An opt-out recorded
 * without a phone applies to every number; one recorded for another number
 * does not carry over after the account's phone changes, because the carrier
 * block is per number too.
 */
export function isPortalSmsOptedOut(
	setting: PortalNotificationSettingStateModel | null,
	phoneE164: string | null,
): boolean {
	if (setting == null || setting.smsOptedOutDate == null) {
		return false;
	}
	return (
		setting.smsOptedOutPhone == null || setting.smsOptedOutPhone === phoneE164
	);
}

/**
 * Which channels one event is actually delivered on for one user. Pure, so
 * the dispatcher, the settings API and the tests share one rule:
 *
 *   1. The master pause switch silences everything.
 *   2. A channel the event is not delivered on (PD-16863) is always off.
 *   3. Each channel takes the user's saved value for the event, else the
 *      default from `defaultPortalNotificationChannels`.
 *   4. Email needs an address; push needs a subscribed browser.
 *   5. A text needs a phone and no STOP on it. With the explicit consent
 *      policy it also needs recorded consent for the account's CURRENT phone,
 *      so a preference alone never sends a text. With the implied policy
 *      (statements, PD-16863) the phone is enough, and the user stops the
 *      texts by unticking Text for the event or replying STOP.
 */
export function resolvePortalNotificationChannels(
	input: ResolvePortalNotificationChannelsInputModel,
): PortalNotificationChannelsModel {
	const off: PortalNotificationChannelsModel = {
		inApp: false,
		email: false,
		sms: false,
		push: false,
	};
	if (input.setting?.notificationsPaused === true) {
		return off;
	}

	const supported =
		input.supportedChannels ?? ALL_PORTAL_NOTIFICATION_CHANNELS;
	const supports = (channel: PortalNotificationChannel): boolean =>
		supported.includes(channel);

	const defaults = defaultPortalNotificationChannels({
		hasEmail: input.hasEmail,
		hasPhone: input.phoneE164 != null,
		hasPushDevice: input.hasPushDevice,
	});
	const explicit = input.explicitPreference;

	const inApp =
		supports(PORTAL_NOTIFICATION_CHANNEL.IN_APP) &&
		(explicit?.inAppEnabled ?? defaults.inApp);
	const email =
		supports(PORTAL_NOTIFICATION_CHANNEL.EMAIL) &&
		input.hasEmail &&
		(explicit?.emailEnabled ?? defaults.email);
	const smsWanted =
		supports(PORTAL_NOTIFICATION_CHANNEL.SMS) &&
		input.phoneE164 != null &&
		(explicit?.smsEnabled ?? defaults.sms);
	const consentSatisfied =
		input.smsConsent === PORTAL_SMS_CONSENT_POLICY.IMPLIED ||
		hasPortalSmsConsent(input.setting, input.phoneE164);
	const sms =
		smsWanted &&
		consentSatisfied &&
		!isPortalSmsOptedOut(input.setting, input.phoneE164);
	const push =
		supports(PORTAL_NOTIFICATION_CHANNEL.PUSH) &&
		input.hasPushDevice &&
		(explicit?.pushEnabled ?? defaults.push);

	return { inApp, email, sms, push };
}
